import { Injectable, NgZone, OnDestroy } from '@angular/core';

/**
 * TimerRegistryService — Centralized management for all setInterval / setTimeout calls.
 *
 * Benefits:
 * - Prevents duplicate timers (re-registering same name auto-clears previous)
 * - clearAll() lets you kill all timers in a single call (soft reload, cleanup)
 * - Named timers are easier to debug than anonymous numeric IDs
 * - runOutsideAngular support prevents unnecessary CD cycles
 *
 * Usage:
 *   timerRegistry.setInterval('clock-tick', () => this.tick(), 1000);
 *   timerRegistry.clearInterval('clock-tick');
 *   timerRegistry.clearAll(); // Called during app reset
 */
@Injectable({ providedIn: 'root' })
export class TimerRegistryService implements OnDestroy {
  private readonly intervals = new Map<string, ReturnType<typeof setInterval>>();
  private readonly timeouts  = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(private readonly ngZone: NgZone) {}

  // ─── setInterval ────────────────────────────────────────────────────────────

  /**
   * Register a named interval. Replaces any existing interval with the same name.
   * @param name     Unique identifier
   * @param fn       Callback
   * @param ms       Interval in milliseconds
   * @param outside  If true, runs outside Angular zone (no CD triggered per tick)
   */
  setInterval(name: string, fn: () => void, ms: number, outside = false): void {
    this.clearInterval(name); // prevent duplicates

    const run = outside
      ? () => this.ngZone.runOutsideAngular(() => this.intervals.set(name, setInterval(fn, ms)))
      : () => this.intervals.set(name, setInterval(fn, ms));

    run();
  }

  clearInterval(name: string): void {
    const id = this.intervals.get(name);
    if (id !== undefined) {
      clearInterval(id);
      this.intervals.delete(name);
    }
  }

  hasInterval(name: string): boolean {
    return this.intervals.has(name);
  }

  // ─── setTimeout ─────────────────────────────────────────────────────────────

  /**
   * Register a named timeout. Replaces any existing timeout with the same name.
   * Auto-cleans from registry after it fires.
   */
  setTimeout(name: string, fn: () => void, ms: number): void {
    this.clearTimeout(name);

    this.timeouts.set(name, setTimeout(() => {
      try {
        fn();
      } finally {
        this.timeouts.delete(name); // auto-cleanup
      }
    }, ms));
  }

  clearTimeout(name: string): void {
    const id = this.timeouts.get(name);
    if (id !== undefined) {
      clearTimeout(id);
      this.timeouts.delete(name);
    }
  }

  hasTimeout(name: string): boolean {
    return this.timeouts.has(name);
  }

  // ─── Bulk Operations ─────────────────────────────────────────────────────────

  /** Clear all registered intervals and timeouts */
  clearAll(): void {
    for (const id of this.intervals.values()) clearInterval(id);
    for (const id of this.timeouts.values())  clearTimeout(id);
    this.intervals.clear();
    this.timeouts.clear();
    console.log('[TimerRegistry] All timers cleared');
  }

  /** Number of active intervals */
  get intervalCount(): number { return this.intervals.size; }

  /** Number of pending timeouts */
  get timeoutCount(): number { return this.timeouts.size; }

  ngOnDestroy(): void {
    this.clearAll();
  }
}
